/**
 * recipientFields.js
 *
 * The one authoritative list of recipient fields an author can set.
 *
 * Everything that describes the CSV import and export is built from the
 * RECIPIENT_CSV_FIELDS table below: the export header, the import parser, the
 * blank template, the in-app help text, and the written specification in
 * docs/recipient_csv_import.md.
 *
 * Adding a field is a one-line change here. Before this file existed the
 * column list was written out three separate times and had drifted, so the
 * export wrote columns the import silently threw away.
 */

/**
 * Languages a survey can be delivered in. The Recipient Browser dropdown and
 * the CSV validator both read this list, so they cannot disagree.
 */
export const RECIPIENT_LANGUAGES = [
    { code: 'en', label: '🇺🇸 English' },
    { code: 'es', label: '🇪🇸 Spanish' },
    { code: 'de', label: '🇩🇪 German' },
    { code: 'fr', label: '🇫🇷 French' },
    { code: 'ja', label: '🇯🇵 Japanese' },
    { code: 'zh', label: '🇨🇳 Chinese' },
    { code: 'pt', label: '🇧🇷 Portuguese' },
    { code: 'it', label: '🇮🇹 Italian' },
    { code: 'ko', label: '🇰🇷 Korean' },
];

export const RECIPIENT_LANGUAGE_CODES = RECIPIENT_LANGUAGES.map((l) => l.code);

/**
 * Channels a recipient can be contacted on.
 *
 * `addressField` names the column that has to hold the address for that
 * channel. `delivers` records whether the send path can actually reach the
 * channel today — "voice" is offered in the UI but the outreach handler has
 * no case for it and fails the send.
 */
export const RECIPIENT_CHANNELS = [
    { value: 'web', label: 'Web', addressField: 'email', inService: true },
    { value: 'email', label: 'Email', addressField: 'email', inService: true },
    { value: 'sms', label: 'SMS', addressField: 'phone_number', inService: true },
    { value: 'whatsapp', label: 'WhatsApp', addressField: 'phone_number', inService: false },
    { value: 'messenger', label: 'Facebook Messenger', addressField: 'facebook_messenger_id', inService: false },
    { value: 'googlechat', label: 'Google Chat', addressField: 'email', inService: false },
    { value: 'voice', label: 'Voice', addressField: 'phone_number', inService: false },
];

/** Channels that can actually deliver a survey today. */
export const RECIPIENT_CHANNELS_IN_SERVICE = RECIPIENT_CHANNELS
    .filter((c) => c.inService)
    .map((c) => c.value);

export const RECIPIENT_CHANNEL_VALUES = RECIPIENT_CHANNELS.map((c) => c.value);

/** Strings accepted for a true/false column, in either case. */
const TRUE_WORDS = ['true', 't', 'yes', 'y', '1'];
const FALSE_WORDS = ['false', 'f', 'no', 'n', '0'];

// ─── Per-field value checkers ────────────────────────────────────────────────
// Each returns { value } on success, or { error } / { warning } to be reported
// against the row number the value came from. A checker is only ever called
// with a non-blank, already-trimmed string.

function checkEmail(v) {
    // Deliberately loose. The job here is to catch a shifted column or a name
    // typed into the email cell, not to re-litigate RFC 5322.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        return { error: `"${v}" is not a valid email address` };
    }
    return { value: v.toLowerCase() };
}

function checkChannel(v) {
    const lower = v.toLowerCase();
    // The send path treats an empty channel and "browser" as the same thing as
    // "web", and the Recipient Browser stores "web". Accept the synonym.
    const normalized = lower === 'browser' ? 'web' : lower;
    if (!RECIPIENT_CHANNEL_VALUES.includes(normalized)) {
        return { error: `"${v}" is not a channel. Use one of: ${RECIPIENT_CHANNEL_VALUES.join(', ')}` };
    }
    if (!RECIPIENT_CHANNELS_IN_SERVICE.includes(normalized)) {
        // Not an error. The channel is a real one and storing it is allowed.
        // The author still has to know the person cannot be reached on it.
        return {
            value: normalized,
            warning: `"${normalized}" is not in service. This person cannot be sent a survey until it is. Channels that deliver today: ${RECIPIENT_CHANNELS_IN_SERVICE.join(', ')}.`,
        };
    }
    return { value: normalized };
}

function checkLanguage(v) {
    const lower = v.toLowerCase();
    if (!RECIPIENT_LANGUAGE_CODES.includes(lower)) {
        // A warning, not an error. The column takes any text, and a survey may
        // be authored in a language this list does not name yet. The import
        // still needs to say that nothing will render in it today.
        return {
            value: lower,
            warning: `language "${v}" is not one of the supported codes (${RECIPIENT_LANGUAGE_CODES.join(', ')}). The survey will not render in it.`,
        };
    }
    return { value: lower };
}

function checkBoolean(v) {
    const lower = v.toLowerCase();
    if (TRUE_WORDS.includes(lower)) return { value: true };
    if (FALSE_WORDS.includes(lower)) return { value: false };
    return { error: `"${v}" is not true or false. Use true/false, yes/no, or 1/0` };
}

function checkLocale(v) {
    // Language tag such as en-US or pt-BR. Underscores are accepted because
    // spreadsheets and older exports produce them.
    const normalized = v.replace('_', '-');
    if (!/^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(normalized)) {
        return { error: `"${v}" is not a locale. Use a tag such as en-US` };
    }
    const [lang, region] = normalized.split('-');
    return { value: region ? `${lang.toLowerCase()}-${region.toUpperCase()}` : lang.toLowerCase() };
}

function checkTimezone(v) {
    // An IANA zone name such as America/New_York, or the bare word UTC.
    //
    // The zone is checked against the browser's own timezone database rather
    // than against a pattern. A pattern only proves the text is shaped like a
    // zone name, so "Mars/Olympus" would pass it and then fail later, at send
    // time, when the schedule could not be worked out.
    if (v.toUpperCase() === 'UTC') return { value: 'UTC' };
    const bad = { error: `"${v}" is not a timezone. Use an IANA name such as America/New_York, or UTC` };
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: v });
    } catch (e) {
        return bad;
    }
    return { value: v };
}

function checkTags(v) {
    const list = v.split(',').map((t) => t.trim()).filter(Boolean);
    return { value: list };
}

function passThrough(v) {
    return { value: v };
}

/**
 * Every column an author can put in an import CSV.
 *
 * required   — the import rejects the row without it
 * aliases    — other header spellings accepted for the same column
 * example    — used in the blank template's comment row and the help text
 * check      — validator, run only on non-blank cells
 * toCsv      — how the column is written on export
 * blankMeans — what a blank cell does, quoted verbatim in the specification
 */
export const RECIPIENT_CSV_FIELDS = [
    {
        column: 'email',
        required: true,
        aliases: ['e-mail', 'email_address'],
        example: 'jane@example.com',
        describe: 'The person’s email address. Also half of the duplicate key.',
        blankMeans: 'The row is rejected.',
        check: checkEmail,
        toCsv: (r) => r.email || '',
    },
    {
        column: 'name',
        required: false,
        aliases: ['full_name', 'display_name'],
        example: 'Jane Doe',
        describe: 'Display name. Also half of the duplicate key, so two spellings of one person’s name make two recipients.',
        blankMeans: 'The recipient is stored with no name.',
        check: passThrough,
        toCsv: (r) => r.name || '',
    },
    {
        column: 'phone_number',
        required: false,
        aliases: ['phone', 'mobile', 'sms', 'sms_number', 'cell'],
        example: '+14135550101',
        describe: 'Phone number in international format. This is the address the send path uses for SMS and WhatsApp.',
        blankMeans: 'Any phone number already on file is kept.',
        check: passThrough,
        toCsv: (r) => r.phone_number || '',
    },
    {
        column: 'whatsapp_id',
        required: false,
        aliases: ['whatsapp', 'whatsapp_number'],
        example: '+14135550101',
        describe: 'WhatsApp address, stored on the recipient record.',
        blankMeans: 'Any WhatsApp address already on file is kept.',
        check: passThrough,
        toCsv: (r) => r.whatsapp_id || r.channel_identities?.whatsapp || '',
    },
    {
        column: 'facebook_messenger_id',
        required: false,
        aliases: ['fb_messenger', 'messenger', 'fb', 'psid'],
        example: '24680135791',
        describe: 'Facebook page-scoped user ID, stored on the recipient record.',
        blankMeans: 'Any Messenger ID already on file is kept.',
        check: passThrough,
        toCsv: (r) => r.facebook_messenger_id || r.channel_identities?.fb_messenger || r.channel_identities?.messenger || '',
    },
    {
        column: 'google_chat_dm',
        required: false,
        aliases: ['googlechat', 'google_chat', 'gchat'],
        example: '',
        // Google Chat is addressed by the person's email, which is already the
        // required email column. This is a stored identifier, not the address,
        // so most accounts should leave it blank.
        describe: 'A Google Chat identifier stored on the recipient record. Not the address — Google Chat is reached at the person’s email.',
        blankMeans: 'Any value already on file is kept. Leaving it blank is normal.',
        check: passThrough,
        toCsv: (r) => r.google_chat_dm || r.channel_identities?.googlechat || '',
    },
    {
        column: 'preferred_channel',
        required: false,
        aliases: ['channel'],
        example: 'email',
        describe: `How to reach this person. One of: ${RECIPIENT_CHANNEL_VALUES.join(', ')}.`,
        blankMeans: 'A new recipient gets "email". An existing recipient keeps the channel already on file.',
        check: checkChannel,
        toCsv: (r) => r.preferred_channel || '',
    },
    {
        column: 'preferred_language',
        required: false,
        aliases: ['language', 'lang'],
        example: 'en',
        describe: `Language the survey is delivered in. One of: ${RECIPIENT_LANGUAGE_CODES.join(', ')}.`,
        blankMeans: 'A new recipient gets "en". An existing recipient keeps the language already on file.',
        check: checkLanguage,
        toCsv: (r) => r.preferred_language || '',
    },
    {
        column: 'locale',
        required: false,
        aliases: [],
        example: 'en-US',
        describe: 'Regional formatting tag, used for dates and numbers.',
        blankMeans: 'A new recipient gets "en-US". An existing recipient keeps the locale already on file.',
        check: checkLocale,
        toCsv: (r) => r.locale || '',
    },
    {
        column: 'timezone',
        required: false,
        aliases: ['tz', 'time_zone'],
        example: 'America/New_York',
        describe: 'IANA timezone name, used when scheduling sends.',
        blankMeans: 'A new recipient gets "UTC". An existing recipient keeps the timezone already on file.',
        check: checkTimezone,
        toCsv: (r) => r.timezone || '',
    },
    {
        column: 'recipient_group',
        required: false,
        aliases: ['group'],
        example: 'Pilot cohort',
        describe: 'Free-text grouping label. Shown as a column in the Recipient Browser and available to the Survey Explorer.',
        blankMeans: 'Any group already on file is kept.',
        check: passThrough,
        toCsv: (r) => r.recipient_group || '',
    },
    {
        column: 'opted_out',
        required: false,
        aliases: ['unsubscribed', 'do_not_contact'],
        example: 'false',
        describe: 'Whether this person has refused contact. An import can set this to true. It can never set it back to false — that has to be done in the Recipient Browser.',
        blankMeans: 'Any existing opt-out is kept. A new recipient is not opted out.',
        check: checkBoolean,
        toCsv: (r) => (r.opted_out === true ? 'true' : r.opted_out === false ? 'false' : ''),
    },
    {
        column: 'tags',
        required: false,
        aliases: ['tag'],
        example: 'pilot,west-coast',
        describe: 'Existing tag names, separated by commas, inside one cell. Tag names that do not already exist are reported and skipped — an import never creates tags.',
        blankMeans: 'Tags already on the recipient are kept.',
        check: checkTags,
        toCsv: (r) => (r.tags || []).join(','),
    },
];

/**
 * Columns written on export so a file can be traced back to the account, and
 * accepted on import so a re-exported file does not error. None of them change
 * anything: the recipient is always matched on email plus name, and the
 * timestamps are set by the server.
 */
export const RECIPIENT_CSV_REFERENCE_COLUMNS = [
    { column: 'global_recipient_id', toCsv: (r) => r.global_recipient_id || '', describe: 'Server-assigned recipient ID.' },
    { column: 'user_id', toCsv: (r) => r.user_id || '', describe: 'Same ID under the name the Users Panel uses.' },
    { column: 'created_at', toCsv: (r) => r.created_at || '', describe: 'When the server first stored this recipient.' },
    { column: 'updated_at', toCsv: (r) => r.updated_at || '', describe: 'When the server last changed this recipient.' },
];

/**
 * Fields that exist on the recipient record but are deliberately not CSV
 * columns. Kept here so the specification can state a reason for each one
 * rather than leaving the omission looking like an oversight.
 */
export const RECIPIENT_NON_IMPORTABLE_FIELDS = [
    { field: 'org_id', reason: 'Taken from the signed-in account. A CSV cannot move a recipient into another organization.' },
    { field: 'name_hash', reason: 'Computed by the database from the name. It is what makes email plus name the duplicate key.' },
    { field: 'staged', reason: 'Adding someone to the staged recipients is what enforces the plan’s recipient cap. An import that wrote this column directly would walk straight past that cap.' },
    { field: 'channel_identities', reason: 'Replaced by the whatsapp_id, facebook_messenger_id, and google_chat_dm columns, which are the fields the rest of the product reads.' },
    { field: 'opted_out_at', reason: 'Stamped by the server when opted_out becomes true.' },
    { field: 'first_seen_at', reason: 'Stamped by the server on first contact.' },
    { field: 'last_activity_at', reason: 'Maintained by the server as the person answers surveys.' },
    { field: 'total_surveys_assigned', reason: 'Counted by the server.' },
    { field: 'total_surveys_completed', reason: 'Counted by the server.' },
];

/** Header spelling → canonical column name, built from the table above. */
export const RECIPIENT_CSV_HEADER_MAP = (() => {
    const map = new Map();
    for (const f of RECIPIENT_CSV_FIELDS) {
        map.set(f.column, f.column);
        for (const a of f.aliases) map.set(a, f.column);
    }
    for (const c of RECIPIENT_CSV_REFERENCE_COLUMNS) map.set(c.column, c.column);
    return map;
})();

/** Canonical column name → field definition. */
export const RECIPIENT_CSV_FIELD_BY_COLUMN = new Map(
    RECIPIENT_CSV_FIELDS.map((f) => [f.column, f])
);

/** Columns that are accepted but change nothing. */
export const RECIPIENT_CSV_REFERENCE_COLUMN_NAMES = new Set(
    RECIPIENT_CSV_REFERENCE_COLUMNS.map((c) => c.column)
);
